import isInteger from 'lodash/isInteger'
import isString from 'lodash/isString'
import toNumber from 'lodash/toNumber'
import type { KlearDateJson } from '@klear/date/json'

const DEFAULT_DISPLAY_FORMAT = 'MM/DD/YYYY'

const DEFAULT_MIN_YEAR = 1900
const DEFAULT_MAX_YEAR = 2100

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
] as const

const MONTH_SHORT_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

export type DateUnit = 'day' | 'days' | 'month' | 'months' | 'year' | 'years'

type DateParts = {
  year: number
  month: number
  day: number
}

function pad(value: number, width = 2): string {
  return `${value}`.padStart(width, '0')
}

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28
  return [4, 6, 9, 11].includes(month) ? 30 : 31
}

function assertValidDateParts(parts: DateParts): void {
  if (!isInteger(parts.year) || !isInteger(parts.month) || !isInteger(parts.day)) {
    throw new Error(`Invalid KlearDate parts: ${JSON.stringify(parts)}`)
  }
  if (parts.month < 1 || parts.month > 12) {
    throw new Error(`Invalid KlearDate month: ${parts.month}`)
  }
  const maxDay = daysInMonth(parts.year, parts.month)
  if (parts.day < 1 || parts.day > maxDay) {
    throw new Error(`Invalid KlearDate day: ${parts.day}`)
  }
}

function partsToYmd(parts: DateParts): KlearDateJson {
  return `${pad(parts.year, 4)}-${pad(parts.month)}-${pad(parts.day)}` as KlearDateJson
}

const MS_PER_DAY = 86_400_000

function daysFromCivil(year: number, month: number, day: number): number {
  let y = year
  y -= month <= 2 ? 1 : 0
  const era = Math.floor(y / 400)
  const yoe = y - era * 400
  const mp = month + (month > 2 ? -3 : 9)
  const doy = Math.floor((153 * mp + 2) / 5) + day - 1
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy
  return era * 146097 + doe - 719468
}

function civilFromDays(days: number): DateParts {
  const z = days + 719468
  const era = Math.floor(z / 146097)
  const doe = z - era * 146097
  const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365)
  const y = yoe + era * 400
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100))
  const mp = Math.floor((5 * doy + 2) / 153)
  const day = doy - Math.floor((153 * mp + 2) / 5) + 1
  const month = mp + (mp < 10 ? 3 : -9)
  return { year: y + (month <= 2 ? 1 : 0), month, day }
}

function parseYmdString(value: string): DateParts | null {
  const trimmed = value.trim()
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/.exec(trimmed)
  if (!match) return null
  const parts = {
    year: toNumber(match[1]),
    month: toNumber(match[2]),
    day: toNumber(match[3])
  }
  try {
    assertValidDateParts(parts)
    return parts
  } catch {
    return null
  }
}

function parseFormattedDate(value: string, format: string): DateParts | null {
  if (format === DEFAULT_DISPLAY_FORMAT) {
    const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value.trim())
    if (!match) return null
    const parts = { year: toNumber(match[3]), month: toNumber(match[1]), day: toNumber(match[2]) }
    try {
      assertValidDateParts(parts)
      return parts
    } catch {
      return null
    }
  }
  if (format === 'YYYY-MM-DD') {
    return parseYmdString(value)
  }
  return null
}

function normalizeUnit(unit: DateUnit | undefined): 'day' | 'month' | 'year' {
  if (unit === undefined || unit === 'day' || unit === 'days') return 'day'
  if (unit === 'month' || unit === 'months') return 'month'
  if (unit === 'year' || unit === 'years') return 'year'
  throw new Error(`Unsupported KlearDate unit: ${unit}`)
}

export class KlearDate {
  static readonly __klearClass = 'KlearDate' as const
  static readonly DEFAULT_FORMAT = DEFAULT_DISPLAY_FORMAT
  static readonly YMD_FORMAT = 'YYYY-MM-DD'

  readonly yearValue: number
  readonly monthValue: number
  readonly dayValue: number

  constructor(ymd: KlearDateJson) {
    const parts = parseYmdString(ymd)
    if (parts === null) {
      throw new Error(`Invalid KlearDate: ${ymd}`)
    }
    this.yearValue = parts.year
    this.monthValue = parts.month
    this.dayValue = parts.day
  }

  get parts(): DateParts {
    return { year: this.yearValue, month: this.monthValue, day: this.dayValue }
  }

  get epochDay(): number {
    return daysFromCivil(this.yearValue, this.monthValue, this.dayValue)
  }

  get quarterValue(): number {
    return Math.floor((this.monthValue - 1) / 3) + 1
  }

  static compare(a: KlearDate, b: KlearDate): number {
    return a.epochDay - b.epochDay
  }

  static min(a: KlearDate, b: KlearDate): KlearDate {
    return a.epochDay <= b.epochDay ? a : b
  }

  static max(a: KlearDate, b: KlearDate): KlearDate {
    return a.epochDay >= b.epochDay ? a : b
  }

  static is(value: unknown): value is KlearDate {
    return value instanceof KlearDate
  }

  static today(): KlearDate {
    // eslint-disable-next-line no-restricted-globals -- sole host-clock read; epoch milliseconds carry no timezone
    const epochMs = Date.now()
    return new KlearDate(partsToYmd(civilFromDays(Math.floor(epochMs / MS_PER_DAY))))
  }

  static daysBetween(start: string | KlearDate | null | undefined, end: string | KlearDate | null | undefined): number {
    const a = KlearDate.parse(start)
    const b = KlearDate.parse(end)
    if (a == null || b == null) return 0
    return b.diff(a, 'day')
  }

  static of(ymd: string): KlearDate {
    const parsed = KlearDate.parse(ymd)
    if (parsed == null) {
      throw new Error(`Invalid KlearDate: ${ymd}`)
    }
    return parsed
  }

  static parse(value: KlearDate): KlearDate
  static parse(
    value: string,
    options?: {
      formats?: readonly string[]
      minYear?: number
      maxYear?: number
    }
  ): KlearDate | undefined
  static parse(
    value: string | KlearDate | null | undefined,
    options?: {
      formats?: readonly string[]
      minYear?: number
      maxYear?: number
    }
  ): KlearDate | undefined
  static parse(
    value: string | KlearDate | null | undefined,
    options?: {
      formats?: readonly string[]
      minYear?: number
      maxYear?: number
    }
  ): KlearDate | undefined {
    if (value == null) return undefined
    if (KlearDate.is(value)) return value
    if (!isString(value) || value.trim() === '') return undefined
    const minYear = options?.minYear ?? DEFAULT_MIN_YEAR
    const maxYear = options?.maxYear ?? DEFAULT_MAX_YEAR
    const inRange = (p: DateParts) => p.year >= minYear && p.year <= maxYear

    for (const format of options?.formats ?? []) {
      const parsed = parseFormattedDate(value, format)
      if (parsed && inRange(parsed)) return new KlearDate(partsToYmd(parsed))
    }

    const parsed = parseYmdString(value)
    return parsed && inRange(parsed) ? new KlearDate(partsToYmd(parsed)) : undefined
  }

  static format(date: KlearDate | undefined | null, format: string = KlearDate.DEFAULT_FORMAT): string {
    if (date == null) return '-'
    return date.toString(format)
  }

  toString(format: string = KlearDate.DEFAULT_FORMAT): string {
    const tokens: Record<string, string> = {
      YYYY: `${this.yearValue}`.padStart(4, '0'),
      YY: `${this.yearValue}`.slice(-2),
      MMMM: MONTH_NAMES[this.monthValue - 1]!,
      MMM: MONTH_SHORT_NAMES[this.monthValue - 1]!,
      MM: pad(this.monthValue),
      M: `${this.monthValue}`,
      DD: pad(this.dayValue),
      D: `${this.dayValue}`
    }
    return format.replace(/YYYY|MMMM|MMM|YY|MM|M|DD|D/g, (token) => tokens[token] ?? token)
  }

  toJSON(): KlearDateJson {
    return partsToYmd(this.parts)
  }

  year(value?: number): number | KlearDate {
    if (value === undefined) return this.yearValue
    return new KlearDate(
      partsToYmd({
        year: value,
        month: this.monthValue,
        day: Math.min(this.dayValue, daysInMonth(value, this.monthValue))
      })
    )
  }

  month(value?: number): number | KlearDate {
    if (value === undefined) return this.monthValue - 1
    const month = value + 1
    return new KlearDate(
      partsToYmd({
        year: this.yearValue,
        month,
        day: Math.min(this.dayValue, daysInMonth(this.yearValue, month))
      })
    )
  }

  date(value?: number): number | KlearDate {
    if (value === undefined) return this.dayValue
    return new KlearDate(partsToYmd({ year: this.yearValue, month: this.monthValue, day: value }))
  }

  day(): number {
    return (((this.epochDay + 4) % 7) + 7) % 7
  }

  daysInMonth(): number {
    return daysInMonth(this.yearValue, this.monthValue)
  }

  add(amount: number, unit: DateUnit): KlearDate {
    const normalized = normalizeUnit(unit)
    if (normalized === 'day') {
      return new KlearDate(partsToYmd(civilFromDays(this.epochDay + amount)))
    }
    if (normalized === 'month') {
      const zeroBasedMonth = this.monthValue - 1 + amount
      const year = this.yearValue + Math.floor(zeroBasedMonth / 12)
      const month = (((zeroBasedMonth % 12) + 12) % 12) + 1
      return new KlearDate(partsToYmd({ year, month, day: Math.min(this.dayValue, daysInMonth(year, month)) }))
    }
    const year = this.yearValue + amount
    return new KlearDate(
      partsToYmd({
        year,
        month: this.monthValue,
        day: Math.min(this.dayValue, daysInMonth(year, this.monthValue))
      })
    )
  }

  subtract(amount: number, unit: DateUnit): KlearDate {
    return this.add(-amount, unit)
  }

  startOf(unit: 'day' | 'month' | 'quarter' | 'year'): KlearDate {
    if (unit === 'day') return this
    if (unit === 'month') return new KlearDate(partsToYmd({ year: this.yearValue, month: this.monthValue, day: 1 }))
    if (unit === 'quarter') {
      return new KlearDate(partsToYmd({ year: this.yearValue, month: (this.quarterValue - 1) * 3 + 1, day: 1 }))
    }
    if (unit === 'year') return new KlearDate(partsToYmd({ year: this.yearValue, month: 1, day: 1 }))
    throw new Error(`Unsupported KlearDate startOf unit: ${unit}`)
  }

  endOf(unit: 'day' | 'month' | 'quarter' | 'year'): KlearDate {
    if (unit === 'day') return this
    if (unit === 'month') {
      return new KlearDate(partsToYmd({ year: this.yearValue, month: this.monthValue, day: this.daysInMonth() }))
    }
    if (unit === 'quarter') {
      const month = this.quarterValue * 3
      return new KlearDate(partsToYmd({ year: this.yearValue, month, day: daysInMonth(this.yearValue, month) }))
    }
    if (unit === 'year') return new KlearDate(partsToYmd({ year: this.yearValue, month: 12, day: 31 }))
    throw new Error(`Unsupported KlearDate endOf unit: ${unit}`)
  }

  diff(other: KlearDate, unit: DateUnit = 'day'): number {
    const normalized = normalizeUnit(unit)
    if (normalized === 'day') return this.epochDay - other.epochDay

    const monthDiff = (this.yearValue - other.yearValue) * 12 + (this.monthValue - other.monthValue)
    if (normalized === 'month') {
      return this.dayValue < other.dayValue ? monthDiff - 1 : monthDiff
    }
    return Math.trunc(monthDiff / 12)
  }

  isSame(other: KlearDate, unit: DateUnit = 'day'): boolean {
    const u = normalizeUnit(unit)
    if (u === 'day') return this.epochDay === other.epochDay
    if (u === 'month') return this.yearValue === other.yearValue && this.monthValue === other.monthValue
    return this.yearValue === other.yearValue
  }

  isBefore(other: KlearDate, unit: DateUnit = 'day'): boolean {
    const u = normalizeUnit(unit)
    if (u === 'month') return this.startOf('month').epochDay < other.startOf('month').epochDay
    if (u === 'year') return this.yearValue < other.yearValue
    return this.epochDay < other.epochDay
  }

  isAfter(other: KlearDate, unit: DateUnit = 'day'): boolean {
    const u = normalizeUnit(unit)
    if (u === 'month') return this.startOf('month').epochDay > other.startOf('month').epochDay
    if (u === 'year') return this.yearValue > other.yearValue
    return this.epochDay > other.epochDay
  }

  isSameOrBefore(other: KlearDate, unit: DateUnit = 'day'): boolean {
    return this.isSame(other, unit) || this.isBefore(other, unit)
  }

  isSameOrAfter(other: KlearDate, unit: DateUnit = 'day'): boolean {
    return this.isSame(other, unit) || this.isAfter(other, unit)
  }
}
